import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated } from '@/features/auth/store/authSelectors';
import { receiveMessage, markConversationRead } from '../store/chatSlice';
import { isSameChatUserId } from '../utils/chatUserId';
import { getAccessToken } from '@/lib/authSession';
import { selectActiveConversationId } from '../store/chatSelectors';
import { markConversationReadApi } from '../services/chatService';

import { WS_URL } from '@/lib/apiOrigin';

/**
 * Socket.io client for real-time chat. Connects when authenticated.
 * Set VITE_CHAT_SOCKET_ENABLED=false to disable (e.g. no backend yet).
 */
export function useChatSocket(userId) {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const activeConversationId = useAppSelector(selectActiveConversationId);
    const socketRef = useRef(null);
    /** Keep latest id for handlers without reconnecting the socket when it changes. */
    const activeConversationIdRef = useRef(activeConversationId);
    activeConversationIdRef.current = activeConversationId;

    const enabled =
        import.meta.env.VITE_CHAT_SOCKET_ENABLED !== 'false' && Boolean(userId) && isAuthenticated;

    useEffect(() => {
        if (!enabled) return undefined;

        const token = getAccessToken();
        const socket = io(WS_URL, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            auth: { token: token ?? '' },
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30000,
            randomizationFactor: 0.5,
        });

        socketRef.current = socket;

        socket.on('chat:message', (payload) => {
            if (!payload?.conversationId || !payload?.id) return;
            const sid = payload.senderId ?? payload.sender_id;
            // authorId is always the actual human who sent — use it for isOwn so entity-scoped
            // messages (senderKind=PILLAR etc. where senderId is the entity id) still match.
            const aid = payload.authorId ?? payload.author_id ?? sid;
            const isOwn = isSameChatUserId(aid, userId);
            const conversationId = String(payload.conversationId);
            dispatch(
                receiveMessage({
                    id: String(payload.id),
                    conversationId,
                    senderId: String(sid ?? ''),
                    authorId: String(aid ?? ''),
                    senderName: String(payload.senderName ?? 'Unknown'),
                    body: String(payload.body ?? ''),
                    createdAt: payload.createdAt || new Date().toISOString(),
                    isOwn,
                }),
            );

            // If the user is already viewing this conversation, treat it as read immediately.
            const viewingId = activeConversationIdRef.current;
            if (
                !isOwn &&
                viewingId &&
                conversationId === String(viewingId) &&
                document.visibilityState === 'visible'
            ) {
                markConversationReadApi(conversationId)
                    .then(() => dispatch(markConversationRead(conversationId)))
                    .catch(() => {});
            }
        });

        return () => {
            socket.removeAllListeners();
            socket.disconnect();
            socketRef.current = null;
        };
    }, [enabled, userId, dispatch]);

    const emitSendMessage = useCallback((payload) => {
        const s = socketRef.current;
        if (s?.connected) {
            s.emit('chat:send', payload);
        }
    }, []);

    const emitJoinConversation = useCallback((conversationId) => {
        const s = socketRef.current;
        if (s?.connected && conversationId) {
            s.emit('chat:join', { conversationId: String(conversationId) });
        }
    }, []);

    return { emitSendMessage, emitJoinConversation, socketRef };
}
